import type { FocusEventHandler, KeyboardEventHandler, RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { _notNil } from '@/littledash';
import type {
  DataTableCellIdCoordinate,
  DataTableCellIndexCoordinate,
  DataTableCellSelectionChangeEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';

export interface UseCellEditModeOptions {
  readonly?: boolean;
  editable?: boolean;
  onActivate?: (content?: string) => void;
  onDeactivate?: (value: string) => void;
}

export interface UseCellEditModeReturn {
  active: boolean;
  handleBlur: FocusEventHandler<HTMLElement>;
  handleKeyDown: KeyboardEventHandler<HTMLElement>;
}

/**
 * Manage editable cell activation, blur, and keyboard navigation
 *
 * This hook handles the complex lifecycle of cell editing:
 * - Activating edit mode when cell is selected
 * - Setting up contentEditable and text selection
 * - Keyboard navigation (Enter, Tab, Escape)
 * - Persisting changes on blur
 * - Workflow-aware navigation
 *
 * Uses AbortController for cleanup.
 *
 * @param coordinate - Cell index coordinate
 * @param idCoordinate - Cell ID coordinate (API IDs)
 * @param inputRef - Ref to the input/contentEditable element
 * @param options - Optional configuration (readonly, custom handlers)
 * @returns Object containing active state and event handlers
 */
export const useCellEditMode = (
  coordinate: DataTableCellIndexCoordinate,
  idCoordinate: DataTableCellIdCoordinate | undefined,
  inputRef: RefObject<HTMLElement | null>,
  options: UseCellEditModeOptions = {}
): UseCellEditModeReturn => {
  const { readonly = false, editable = true, onActivate, onDeactivate } = options;
  const { dataTableService } = useDataTable();
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (_notNil(idCoordinate) && !readonly && editable) {
      const abortController = new AbortController();

      const cellSelectionChangeListener = (event: DataTableCellSelectionChangeEvent) => {
        const fromCoordinate = event.detail.selection.from;

        if (
          event.detail.activate &&
          inputRef.current != null &&
          fromCoordinate.column === coordinate.column &&
          fromCoordinate.row === coordinate.row
        ) {
          if (_notNil(onActivate)) {
            // Custom activation handler (e.g., for TimestampCell picker)
            onActivate(event.detail.content);
          } else {
            // Default contentEditable activation
            const element = inputRef.current as HTMLDivElement;
            element.setAttribute('data-active', 'true');
            element.contentEditable = 'true';

            const content =
              (event.detail.content?.length ?? 0) > 0
                ? event.detail.content
                : dataTableService.cellData(idCoordinate)?.value;

            if ('innerText' in element) {
              element.innerText = content ?? '';
            }

            // Select all text
            const selection = window.getSelection();
            const range = document.createRange();
            selection?.removeAllRanges();
            range.selectNodeContents(element);
            range.collapse(false);
            selection?.addRange(range);
            element.focus();
          }

          setActive(true);
        }
      };

      dataTableService.subscribe(
        DataTableEvent.CellSelectionChange,
        cellSelectionChangeListener,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, coordinate, idCoordinate, inputRef, readonly, editable, onActivate]);

  const handleBlur: FocusEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (!readonly && _notNil(idCoordinate) && editable) {
        const element = event.target as HTMLDivElement;
        element.contentEditable = 'false';
        element.setAttribute('data-active', 'false');

        const value = 'innerText' in element ? (element.innerText?.trim() ?? '') : '';

        if (_notNil(onDeactivate)) {
          onDeactivate(value);
        } else {
          dataTableService.updateCells([
            {
              column_id: idCoordinate.column,
              row_id: idCoordinate.row,
              value: value.length === 0 ? null : value,
            },
          ]);
        }

        setActive(false);
      }
    },
    [dataTableService, idCoordinate, readonly, editable, onDeactivate]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (readonly) {
        return;
      }

      switch (event.key) {
        case 'Escape': {
          event.stopPropagation();
          event.preventDefault();
          if (_notNil(inputRef.current) && _notNil(idCoordinate)) {
            const element = inputRef.current as HTMLDivElement;
            if ('innerText' in element) {
              element.innerText = dataTableService.cellData(idCoordinate)?.value ?? '';
            }
            element.blur();
          }
          break;
        }
        case 'Enter': {
          if (!event.ctrlKey) {
            event.stopPropagation();
            event.preventDefault();
            inputRef.current?.blur();
            if (_notNil(idCoordinate)) {
              const workflowStepExecuted = dataTableService.workflowService.workflowActionExit(idCoordinate.column);
              if (!workflowStepExecuted) {
                dataTableService.moveSelection(event.shiftKey ? 'UP' : 'DOWN');
              }
            }
          }
          break;
        }
        case 'Tab': {
          event.stopPropagation();
          event.preventDefault();
          inputRef.current?.blur();
          if (_notNil(idCoordinate)) {
            const workflowStepExecuted = dataTableService.workflowService.workflowActionExit(idCoordinate.column);
            if (!workflowStepExecuted) {
              dataTableService.moveSelection(event.shiftKey ? 'LEFT' : 'RIGHT');
            }
          }
          break;
        }
      }
    },
    [dataTableService, inputRef, idCoordinate, readonly]
  );

  return {
    active,
    handleBlur,
    handleKeyDown,
  };
};
